import React from 'react';
import type { DonneesPV, PVEtudiant, PVUEStructure, MembreJury } from '../../services/pvDeliberationApi';
import { type PVModele, type PVModeleOptions, buildPVJuryClassiqueHTML, buildPVSyntheseValidesHTML, buildPVMinisterielHTML, buildPVSyntheseBiSessionHTML } from './PVModeles';

// ─── Helpers ────────────────────────────────────────────────────────────────

const fN = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return '—';
  return v.toFixed(2).replace('.', ',');
};

const sessionLabel = (s: string) => (s === 'rattrapage' ? 'RATTRAPAGE' : 'ORDINAIRE');

// ─── buildPVHTML — génère le HTML complet pour window.open() ────────────────

export function buildPVHTML(data: DonneesPV, dateDeliberation: string): string {
  const { info_classe, annee_academique, semestre, session, ues_structure, etudiants, statistiques, jury } = data;

  // Convertit l'ISO en libellé FR ("19 juin 2026") ; si déjà formaté ou
  // illisible, on garde la chaîne brute.
  const dateLibelle = (() => {
    if (!dateDeliberation) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(dateDeliberation)) {
      const d = new Date(dateDeliberation.slice(0, 10) + 'T00:00:00');
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
      }
    }
    return dateDeliberation;
  })();

  // Compter le nombre total de colonnes pour déterminer la taille de police
  let totalMatCols = 0;
  ues_structure.forEach(ue => { totalMatCols += ue.matieres.length; });
  const totalCols = 3 + totalMatCols + ues_structure.length + 4; // N, Mat, Nom + moy_ecue per mat + moy_ue per UE + MoyGen, Cr, Rang, Dec
  const fontSize = totalCols > 25 ? '5.5px' : totalCols > 18 ? '6.5px' : '7.5px';

  // En-têtes de colonnes
  let ueHeaderRow = '';
  let matHeaderRow = '';
  ues_structure.forEach(ue => {
    const colspan = ue.matieres.length + 1; // matières + moy UE
    ueHeaderRow += `<th colspan="${colspan}" style="background:#1F4788;color:#fff;padding:2px 1px;border:1px solid #999;text-align:center;font-size:${fontSize};">${ue.code_ue}</th>`;
    ue.matieres.forEach(m => {
      matHeaderRow += `<th style="background:#FFD700;padding:2px 1px;border:1px solid #999;text-align:center;font-size:${fontSize};writing-mode:horizontal-tb;">${m.code_matiere}</th>`;
    });
    matHeaderRow += `<th style="background:#E8E8E8;padding:2px 1px;border:1px solid #999;text-align:center;font-size:${fontSize};font-weight:bold;">Moy<br/>UE</th>`;
  });

  // Lignes étudiants
  let studentRows = '';
  etudiants.forEach((etud: PVEtudiant, idx: number) => {
    const bgColor = idx % 2 === 0 ? '#fff' : '#f9f9f9';
    const rs = etud.resultat_semestre;
    const isAdmis = rs?.valide;
    const decColor = isAdmis ? '#006600' : '#CC0000';

    let row = `<tr style="background:${bgColor};">`;
    row += `<td style="border:1px solid #999;padding:1px 3px;text-align:center;">${idx + 1}</td>`;
    row += `<td style="border:1px solid #999;padding:1px 3px;text-align:center;white-space:nowrap;">${etud.matricule}</td>`;
    row += `<td style="border:1px solid #999;padding:1px 3px;white-space:nowrap;">${etud.nom} ${etud.prenoms}</td>`;

    ues_structure.forEach((ue: PVUEStructure) => {
      ue.matieres.forEach(m => {
        const n = etud.notes[String(m.matiere_id)];
        const val = n ? (n.absent ? '<span style="color:#C00;">AB</span>' : fN(n.moyenne_ecue)) : '—';
        row += `<td style="border:1px solid #999;padding:1px 2px;text-align:center;">${val}</td>`;
      });
      const ueRes = etud.ues_results[String(ue.ue_id)];
      const ueVal = ueRes ? fN(ueRes.moyenne_ue) : '—';
      const ueBg = ueRes && ueRes.validee ? '#e6ffe6' : (ueRes && ueRes.moyenne_ue !== null && ueRes.moyenne_ue < 10 ? '#ffe6e6' : '#f0f0f0');
      row += `<td style="border:1px solid #999;padding:1px 2px;text-align:center;font-weight:bold;background:${ueBg};">${ueVal}</td>`;
    });

    row += `<td style="border:1px solid #999;padding:1px 2px;text-align:center;font-weight:bold;">${rs ? fN(rs.moyenne_semestre) : '—'}</td>`;
    row += `<td style="border:1px solid #999;padding:1px 2px;text-align:center;">${rs ? rs.credits_obtenus + '/' + rs.credits_requis : '—'}</td>`;
    row += `<td style="border:1px solid #999;padding:1px 2px;text-align:center;">${rs?.rang ?? '—'}</td>`;
    row += `<td style="border:1px solid #999;padding:1px 2px;text-align:center;font-weight:bold;color:${decColor};">${rs?.decision ?? '—'}</td>`;
    row += '</tr>';
    studentRows += row;
  });

  // Jury
  let juryRows = '';
  jury.forEach((m: MembreJury, i: number) => {
    const nom = m.titre_academique ? `${m.titre_academique} ${m.nom_complet}` : m.nom_complet;
    juryRows += `<tr>
      <td style="border:1px solid #999;padding:4px 8px;text-align:center;">${i + 1}</td>
      <td style="border:1px solid #999;padding:4px 8px;">${nom}</td>
      <td style="border:1px solid #999;padding:4px 8px;">${m.fonction_display}</td>
      <td style="border:1px solid #999;padding:4px 8px;height:40px;"></td>
    </tr>`;
  });

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>PV Délibération — ${info_classe.classe_code} S${semestre} ${sessionLabel(session)}</title>
<style>
  @page { size: A4 landscape; margin: 8mm 6mm; }
  @media print {
    .pv-no-print { display: none !important; }
    html, body { margin: 0 !important; padding: 0 !important; }
  }
  body { font-family: 'Times New Roman', serif; font-size: ${fontSize}; color: #000; margin: 0; padding: 8px; }
  table { border-collapse: collapse; }
  .header-table { width: 100%; margin-bottom: 4px; }
  .header-table td { vertical-align: top; padding: 0; }
  .title-block { text-align: center; font-weight: bold; font-size: 14px; padding: 6px 0; border: 2px solid #1F4788; margin: 4px 0; background: #f0f4ff; color: #1F4788; letter-spacing: 1px; }
  .info-block { width: 100%; margin-bottom: 6px; }
  .info-block td { padding: 2px 8px; font-size: 9px; }
  .info-label { font-weight: bold; }
  .results-table { width: 100%; font-size: ${fontSize}; }
  .results-table th, .results-table td { font-size: ${fontSize}; }
  .stats-block { margin: 6px 0; padding: 6px 12px; background: #f0f4ff; border: 1px solid #1F4788; font-size: 9px; }
  .jury-table { width: 100%; margin-top: 8px; font-size: 9px; }
  .jury-table th { background: #1F4788; color: #fff; padding: 4px 8px; border: 1px solid #999; }
  .footer { margin-top: 10px; font-size: 9px; text-align: right; }
</style>
</head>
<body>

<!-- En-tête -->
<table class="header-table">
  <tr>
    <td style="width:40%;font-size:8px;line-height:1.3;">
      <div><strong>RÉPUBLIQUE DU CONGO</strong></div>
      <div style="font-style:italic;">Unité * Travail * Progrès</div>
      <div>-------</div>
      <div>Ministère de l'Enseignement Supérieur,</div>
      <div>de la Recherche Scientifique</div>
      <div>et de l'Innovation Technologique</div>
    </td>
    <td style="width:30%;text-align:center;font-size:8px;line-height:1.4;">
      <div style="font-weight:bold;font-size:10px;">INSTITUT INTERNATIONAL</div>
      <div style="font-weight:bold;font-size:10px;">POLYTECHNIQUE & COMMERCE 2I</div>
      <div style="margin-top:2px;">B.P. ---- Pointe-Noire</div>
      <div>Tél: +242 --- --- ---</div>
    </td>
    <td style="width:30%;text-align:right;font-size:8px;">
      <div style="text-align:center;">
        <img src="/logo.png" alt="Logo" style="height:50px;margin-bottom:2px;" onerror="this.style.display='none'" />
      </div>
    </td>
  </tr>
</table>

<div class="title-block">PROCÈS-VERBAL DE DÉLIBÉRATION</div>

<!-- Infos -->
<table class="info-block">
  <tr>
    <td><span class="info-label">Année Académique :</span> ${annee_academique}</td>
    <td><span class="info-label">Session :</span> ${sessionLabel(session)}</td>
    <td><span class="info-label">Semestre :</span> S${semestre}</td>
  </tr>
  <tr>
    <td><span class="info-label">Pôle :</span> ${info_classe.pole_nom}</td>
    <td><span class="info-label">Option :</span> ${info_classe.option_nom} (${info_classe.option_code})</td>
    <td><span class="info-label">Classe :</span> ${info_classe.classe_code}</td>
  </tr>
  <tr>
    <td><span class="info-label">Parcours :</span> ${info_classe.parcours_nom || '—'}</td>
    <td><span class="info-label">Cycle :</span> ${info_classe.cycle}</td>
    <td><span class="info-label">Date Délibération :</span> ${dateLibelle || '___/___/______'}</td>
  </tr>
</table>

<!-- Tableau des résultats -->
<table class="results-table">
  <thead>
    <tr>
      <th rowspan="2" style="background:#1F4788;color:#fff;padding:2px;border:1px solid #999;width:20px;">N°</th>
      <th rowspan="2" style="background:#1F4788;color:#fff;padding:2px;border:1px solid #999;">Matricule</th>
      <th rowspan="2" style="background:#1F4788;color:#fff;padding:2px;border:1px solid #999;">Nom & Prénoms</th>
      ${ueHeaderRow}
      <th rowspan="2" style="background:#1F4788;color:#fff;padding:2px;border:1px solid #999;">Moy<br/>Gén</th>
      <th rowspan="2" style="background:#1F4788;color:#fff;padding:2px;border:1px solid #999;">Crédits</th>
      <th rowspan="2" style="background:#1F4788;color:#fff;padding:2px;border:1px solid #999;">Rang</th>
      <th rowspan="2" style="background:#1F4788;color:#fff;padding:2px;border:1px solid #999;">Décision</th>
    </tr>
    <tr>
      ${matHeaderRow}
    </tr>
  </thead>
  <tbody>
    ${studentRows}
  </tbody>
</table>

<!-- Statistiques -->
<div class="stats-block">
  <strong>RÉSUMÉ :</strong>&nbsp;&nbsp;
  Effectif Total : <strong>${statistiques.effectif_total}</strong> &nbsp;|&nbsp;
  Admis : <strong style="color:#006600;">${statistiques.nombre_admis}</strong> &nbsp;|&nbsp;
  Ajournés : <strong style="color:#CC0000;">${statistiques.nombre_ajournes}</strong> &nbsp;|&nbsp;
  Taux de Réussite : <strong>${statistiques.taux_reussite.toFixed(2)}%</strong>
</div>

<!-- Jury -->
<table class="jury-table">
  <thead>
    <tr>
      <th colspan="4" style="text-align:left;font-size:10px;padding:6px 8px;">MEMBRES DU JURY DE DÉLIBÉRATION</th>
    </tr>
    <tr>
      <th style="width:30px;">N°</th>
      <th>Nom et Prénoms</th>
      <th>Qualité / Fonction</th>
      <th style="width:120px;">Signature</th>
    </tr>
  </thead>
  <tbody>
    ${juryRows || '<tr><td colspan="4" style="border:1px solid #999;padding:8px;text-align:center;font-style:italic;color:#999;">Aucun membre de jury configuré pour ce pôle</td></tr>'}
  </tbody>
</table>

<div class="footer">
  Fait à Pointe-Noire, le ${dateLibelle || '___/___/______'}
</div>

</body>
</html>`;
}

// ─── Composant React pour aperçu modal ──────────────────────────────────────

export function buildPVByModele(
  data: DonneesPV,
  dateDeliberation: string,
  modele: PVModele,
  options: PVModeleOptions = {},
): string {
  switch (modele) {
    case 'jury_classique':
      return buildPVJuryClassiqueHTML(data, dateDeliberation, options.salle);
    case 'synthese_valides':
      return buildPVSyntheseValidesHTML(data, dateDeliberation, options.salle);
    case 'ministeriel':
      return buildPVMinisterielHTML(data, dateDeliberation, options);
    case 'synthese_bi_session':
      return buildPVSyntheseBiSessionHTML(data, dateDeliberation, options);
    case 'detaille':
    default:
      return buildPVHTML(data, dateDeliberation);
  }
}

/**
 * Combine plusieurs PV HTMLs (un par classe) en un seul document imprimable
 * où chaque PV occupe ses propres pages, séparées par un saut de page.
 *
 * Stratégie : on extrait le contenu de `<body>` de chaque PV et on les
 * concatène dans un wrapper qui réutilise les `<style>` du premier (tous les
 * modèles partagent les mêmes classes utilitaires) + un saut de page entre
 * chaque section.
 */
export function buildPVBatchHTML(htmls: string[]): string {
  if (htmls.length === 0) return '';
  if (htmls.length === 1) return htmls[0];

  const bodies = htmls.map((html) => {
    const m = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    return m ? m[1] : html;
  });
  // On reprend les <style> de chaque PV (Mode détaillé n'a pas la même mise en
  // page que le modèle ministériel) pour ne rien perdre.
  const styles = htmls
    .map((html) => {
      const m = html.match(/<style[\s\S]*?<\/style>/gi);
      return m ? m.join('\n') : '';
    })
    .filter(Boolean)
    .join('\n');

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
<title>PV de Délibération — Lot multi-classes</title>
${styles}
<style>
  .pv-page { page-break-after: always; }
  .pv-page:last-child { page-break-after: auto; }
  @media print { .pv-page { page-break-after: always; } }
</style>
</head><body>
${bodies.map((b) => `<div class="pv-page">${b}</div>`).join('\n')}
</body></html>`;
}

interface PVPreviewProps {
  data: DonneesPV;
  dateDeliberation: string;
  modele?: PVModele;
  options?: PVModeleOptions;
}

const PVPreview: React.FC<PVPreviewProps> = ({ data, dateDeliberation, modele = 'detaille', options = {} }) => {
  const html = buildPVByModele(data, dateDeliberation, modele, options);

  return (
    <div style={{ width: '100%', overflow: 'auto' }}>
      <iframe
        srcDoc={html}
        style={{ width: '100%', minHeight: '80vh', border: 'none' }}
        title="Aperçu PV de Délibération"
      />
    </div>
  );
};

export default PVPreview;
